import type { PayloadAction } from '@reduxjs/toolkit';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import type { ViewTypes } from 'src/helper/shared-types';
import { FETCH_DETAILS_VIEW_TYPES, isMarketScopedViewType } from 'src/redux/list-view/constants';
import { withDrawnDefaultColumns } from 'src/redux/list-view/drawnDefaultColumns';
import {
  type AppliedFilterType,
  type CalendarFilterObjType,
  type ColumnObjType,
  type FetchAllViewsResponse,
  type GroupByDetailsOptionObj,
  type InitialStateType,
  listViewInitialState,
  type ViewDetailsType,
} from 'src/redux/list-view/types';
import type { QuoteFilterStatus } from '../quotes/types';

// Defaults the market filter to the user's markets; never overrides a selection.
export const seedMarkets = (
  state: InitialStateType,
  markets: Array<{ id: string; name: string }> | undefined,
) => {
  if (state.selectedMarkets.length > 0 || !markets?.length) return;
  state.selectedMarkets = markets.map((market) => ({ label: market.name, value: market.id }));
};

const reducers = {
  clearListingConfigData: (
    state: InitialStateType,
    action: PayloadAction<{ viewType: ViewTypes }>,
  ) => {
    const viewType = action?.payload?.viewType;

    // Restore active view's config from reference before preserving maps
    // This discards unsaved filter/column/grouping changes on nav-away
    const activeViewId = state[viewType].viewId;
    if (activeViewId) {
      const referenceConfig = state[viewType].viewDetailsReferenceMap[activeViewId];
      if (referenceConfig) {
        state[viewType].viewDetailsMap[activeViewId] = { ...referenceConfig };
      }
    }

    const viewList = {
      isViewListLoading: false,
      data: [...(state[viewType]?.viewList?.data || [])],
    };
    const viewDetailsMap = { ...(state[viewType]?.viewDetailsMap || {}) };
    const viewDetailsReferenceMap = { ...(state[viewType]?.viewDetailsReferenceMap || {}) };
    // Sentinel views (no UUID in URL) have a single fixed config — preserve viewId so
    // selectors continue to resolve on re-entry without needing a restoreViewId dispatch.
    const isSentinel = !FETCH_DETAILS_VIEW_TYPES.includes(viewType) && viewType !== 'roles';
    const preservedViewState = isSentinel
      ? {
          viewId: state[viewType].viewId,
          isViewDetailsFetched: state[viewType].isViewDetailsFetched,
        }
      : {};
    state[viewType] = {
      ...listViewInitialState?.[viewType],
      viewList,
      viewDetailsMap,
      viewDetailsReferenceMap,
      ...preservedViewState,
    };
  },
  clearViewList: (state: InitialStateType, action: PayloadAction<ViewTypes>) => {
    state[action?.payload].viewList = listViewInitialState[action?.payload].viewList;
  },
  applyGroupBy: (
    state: InitialStateType,
    action: PayloadAction<{
      item: { value: string; label: string };
      viewType: ViewTypes;
      viewId: string;
    }>,
  ) => {
    const { viewType, viewId, item } = action.payload;
    const newValue = { ...(item || {}) };
    const currentDetails = state[viewType]?.viewDetailsMap?.[viewId];
    const currentGroupValue = currentDetails?.grouping?.g_by?.value;

    if (currentDetails?.grouping && !isEmpty(newValue) && newValue?.value !== currentGroupValue) {
      state[viewType].groupByDetails = [];
      state[viewType].viewDetailsMap[viewId] = {
        ...(currentDetails || {}),
        grouping: {
          ...(currentDetails?.grouping || {}),
          g_by: newValue,
        } as ViewDetailsType['grouping'],
      };
    }
  },
  clearGroupByDetails: (state: InitialStateType, action: PayloadAction<ViewTypes>) => {
    if (!isEmpty(state?.[action?.payload]?.groupByDetails)) {
      state[action?.payload].groupByDetails = [];
    }
  },
  clearGroupBy: (
    state: InitialStateType,
    action: PayloadAction<{ viewType: ViewTypes; viewId: string }>,
  ) => {
    const { viewType, viewId } = action.payload;
    const currentDetails = state[viewType]?.viewDetailsMap?.[viewId];
    if (currentDetails?.grouping) {
      state[viewType].viewDetailsMap[viewId] = {
        ...(currentDetails || {}),
        grouping: {
          ...(currentDetails.grouping || {}),
          g_by: { value: 'none', label: 'None' },
        } as ViewDetailsType['grouping'],
      };
    }
  },
  // Clear the single global group-view loading flag. `fetchingGroupByDetails` is otherwise
  // only reset by the getSelectedGroupByDetails lifecycle reducers; when a request is aborted
  // on unmount there is no successor to reset it, so the consuming hook dispatches this on
  // teardown to avoid stranding a sibling listing on a permanent skeleton (ORI-538).
  resetFetchingGroupByDetails: (state: InitialStateType) => {
    state.fetchingGroupByDetails = false;
  },
  setIsViewListLoading: (
    state: InitialStateType,
    action: PayloadAction<{ viewType: ViewTypes; value: boolean }>,
  ) => {
    state[action.payload.viewType].viewList.isViewListLoading = action.payload.value || false;
  },
  updateColumns: (
    state: InitialStateType,
    action: PayloadAction<{
      columnObject: Array<ColumnObjType>;
      viewType: ViewTypes;
      viewId: string;
    }>,
  ) => {
    const { viewType, viewId, columnObject } = action.payload;
    if (state[viewType]?.viewDetailsMap?.[viewId]) {
      state[viewType].viewDetailsMap[viewId].columns = columnObject;
    }
  },
  applySearch: (
    state: InitialStateType,
    action: PayloadAction<{ searchInput: string; type: ViewTypes }>,
  ) => {
    if (state[action.payload.type]?.search) {
      state[action.payload.type].search.searchInput = action.payload.searchInput;
    }
  },
  applySort: (
    state: InitialStateType,
    action: PayloadAction<{ sort: string; type: ViewTypes }>,
  ) => {
    state[action?.payload?.type].sort.sortKey = action?.payload?.sort;
  },
  setIsViewDetailsFetched: (
    state: InitialStateType,
    action: PayloadAction<{ viewDetailsFetched: boolean; viewType: ViewTypes }>,
  ) => {
    if (state[action?.payload?.viewType]) {
      state[action?.payload?.viewType].isViewDetailsFetched = action.payload?.viewDetailsFetched;
    }
  },
  setIsViewDetailsFetching: (state: InitialStateType, action: PayloadAction<boolean>) => {
    state.fetchingViewDetails = action.payload;
  },
  setToggleGroup: (
    state: InitialStateType,
    action: PayloadAction<{ groupKey: string; value: boolean; viewType: ViewTypes }>,
  ) => {
    const { groupKey, value } = action.payload;
    const newGroupCollapseSettings = {
      ...(state[action?.payload?.viewType].groupCollapseSettings || {}),
    };
    newGroupCollapseSettings[groupKey] = value;
    state[action?.payload?.viewType].groupCollapseSettings = newGroupCollapseSettings;
  },
  clearFilters: (
    state: InitialStateType,
    action: PayloadAction<{ viewType: ViewTypes; viewId: string; skipMarketReset?: boolean }>,
  ) => {
    const { viewType, viewId, skipMarketReset } = action.payload;
    state[viewType].sort = listViewInitialState?.[viewType]?.sort;
    state[viewType].search = listViewInitialState?.[viewType]?.search;
    state[viewType].filtersDetails = listViewInitialState?.[viewType]?.filtersDetails;

    const currentDetails = state[viewType]?.viewDetailsMap?.[viewId];
    if (currentDetails?.filters) {
      const newObj = {
        ...(currentDetails?.filters || {}),
        applied: {
          ...(currentDetails?.filters?.applied || {}),
          fl_group: [],
          group_op: null,
        },
      } as ViewDetailsType['filters'];
      state[viewType].viewDetailsMap[viewId] = {
        ...currentDetails,
        filters: newObj,
      };
    }
    if (viewType === 'calendar' && currentDetails) {
      const calendarInitial = listViewInitialState?.['calendar'];
      // Reset calendar filter to a sensible default
      state[viewType].viewDetailsMap[viewId] = {
        ...(state[viewType].viewDetailsMap[viewId] || {}),
        calendarFilter: {
          technician: null,
          calendarMode: { value: 'weekday', label: 'Weekday' },
          settings: { limitToWorkingHours: true },
        },
      };
      state[viewType].calendarDatePickerSelectedDate =
        calendarInitial?.calendarDatePickerSelectedDate;
    }
    state[viewType].groupByDetails = listViewInitialState?.[viewType].groupByDetails;

    // ORI-496: the market selection is a global, cross-view scope (state.selectedMarkets)
    // that the per-view resets above don't touch. Clearing filters on a market-scoped list
    // must also clear it so the "No Results Found" empty-state action restores the full list.
    // Gated so clearing filters on a non-market list never wipes the user's market scope.
    // skipMarketReset lets a market-scoped view (the unassigned-Properties view, which is
    // market_assignment-driven) clear its own filters without touching the shared market scope.
    if (isMarketScopedViewType(viewType) && !skipMarketReset) {
      state.selectedMarkets = listViewInitialState.selectedMarkets;
    }
  },
  updateFilter: (
    state: InitialStateType,
    action: PayloadAction<{ filter: AppliedFilterType; viewType: ViewTypes; viewId: string }>,
  ) => {
    const { viewType, viewId, filter } = action.payload;
    const currentDetails = state[viewType]?.viewDetailsMap?.[viewId];
    if (currentDetails?.filters) {
      state[viewType].viewDetailsMap[viewId] = {
        ...(currentDetails || {}),
        filters: {
          ...(currentDetails?.filters || {}),
          applied: filter,
        } as ViewDetailsType['filters'],
      };
    }
  },
  updateFilterComponent: (
    state: InitialStateType,
    action: PayloadAction<{ filter: AppliedFilterType; viewType: ViewTypes }>,
  ) => {
    state[action?.payload?.viewType].filtersDetails.filterComponent = action?.payload?.filter;
  },
  promptListViewHasChanges: (
    state: InitialStateType,
    action: PayloadAction<{ key: string; viewType: ViewTypes; viewId: string }>,
  ) => {
    const { key, viewType, viewId } = action.payload;
    const moduleState = state[viewType];
    const current = moduleState?.viewDetailsMap?.[viewId];
    const reference = moduleState?.viewDetailsReferenceMap?.[viewId];

    if (viewType === 'calendar') {
      if (key === 'calendarMode') {
        moduleState.hasListViewChanged.hasCalendarModeChanged = !isEqual(
          reference?.calendarFilter?.[key as keyof CalendarFilterObjType],
          current?.calendarFilter?.[key as keyof CalendarFilterObjType],
        );
      }
      if (key === 'technician') {
        moduleState.hasListViewChanged.hasTechnicianChanged = !isEqual(
          reference?.calendarFilter?.[key as keyof CalendarFilterObjType],
          current?.calendarFilter?.[key as keyof CalendarFilterObjType],
        );
      }
      if (key === 'settings') {
        moduleState.hasListViewChanged.hasCalendarSettingsChanged = !isEqual(
          reference?.calendarFilter?.[key as keyof CalendarFilterObjType],
          current?.calendarFilter?.[key as keyof CalendarFilterObjType],
        );
      }
    } else {
      if (key === 'filters') {
        moduleState.hasListViewChanged.hasFilterChanged = !isEqual(
          reference?.filters,
          current?.filters,
        );
      }
      if (key === 'grouping') {
        moduleState.hasListViewChanged.hasGroupingChanged = !isEqual(
          reference?.grouping,
          current?.grouping,
        );
      }
      if (key === 'column') {
        moduleState.hasListViewChanged.hasColumnsChanged = !isEqual(
          reference?.columns,
          current?.columns,
        );
      }
    }
  },

  addDuplicateView: (
    state: InitialStateType,
    action: PayloadAction<{
      res: {
        view: { label: string; value: ViewTypes };
        is_able_to_pin: boolean;
        is_default: boolean;
        is_deletable: boolean;
        is_editable: boolean;
        is_pinned: boolean;
      };
      viewType: ViewTypes;
      viewDetails: ViewDetailsType;
    }>,
  ) => {
    const { viewType, res, viewDetails } = action.payload;
    const newViewId = res?.view?.value;
    if (viewType === undefined) return;

    state[viewType].viewList.data = [
      ...state[viewType].viewList.data,
      {
        view: { ...(res?.view || {}) },
        is_default: false,
        is_pinned: false,
        is_deletable: res?.is_deletable,
        is_editable: res?.is_editable,
        is_able_to_pin: res?.is_able_to_pin,
      },
    ];

    if (newViewId && viewDetails) {
      state[viewType].viewDetailsMap[newViewId] = { ...viewDetails };
      state[viewType].viewDetailsReferenceMap[newViewId] = { ...viewDetails };
    }
  },

  addNewViewItem: (
    state: InitialStateType,
    action: PayloadAction<{
      res: {
        view: { label: string; value: string };
        is_able_to_pin: boolean;
        is_default: boolean;
        is_deletable: boolean;
        is_editable: boolean;
        is_pinned: boolean;
      };
      viewType: ViewTypes;
      viewDetails: ViewDetailsType;
    }>,
  ) => {
    const { viewType, res, viewDetails } = action.payload;
    const newViewId = res?.view?.value;

    state[viewType].viewList.data = [
      ...(state[viewType]?.viewList?.data || []),
      {
        view: { ...res?.view },
        is_default: false,
        is_pinned: false,
        is_deletable: res?.is_deletable,
        is_editable: res?.is_editable,
        is_able_to_pin: res?.is_able_to_pin,
      },
    ];

    if (newViewId && viewDetails) {
      state[viewType].viewDetailsMap[newViewId] = { ...viewDetails };
      state[viewType].viewDetailsReferenceMap[newViewId] = { ...viewDetails };
    }

    // Restore the source view's config from reference so it reverts to its original state
    const currentViewId = state[viewType].viewId;
    const referenceConfig = state[viewType].viewDetailsReferenceMap[currentViewId];
    if (currentViewId && referenceConfig) {
      state[viewType].viewDetailsMap[currentViewId] = { ...referenceConfig };
    }

    state[viewType].hasListViewChanged = listViewInitialState?.[viewType]?.hasListViewChanged;
  },

  setViewAsDefault: (
    state: InitialStateType,
    action: PayloadAction<{
      res: {
        view: {
          value: string;
        };
        is_default: true;
      };
      viewType: ViewTypes;
    }>,
  ) => {
    const newData = state[action?.payload?.viewType]?.viewList?.data?.map((item) => ({
      ...item,
      is_default: item?.view?.value === action?.payload?.res?.view?.value,
    }));
    state[action?.payload?.viewType].viewList.data = newData;
  },

  reNameViewItem: (
    state: InitialStateType,
    action: PayloadAction<{ res: { view: { value: string; label: string } }; viewType: ViewTypes }>,
  ) => {
    const newData = state[action?.payload?.viewType]?.viewList?.data?.map((item) => {
      if (item?.view?.value === action?.payload?.res?.view?.value) {
        return {
          ...item,
          view: {
            label: action?.payload?.res?.view?.label,
            value: action?.payload?.res?.view?.value,
          },
        };
      }
      return item;
    });
    state[action?.payload?.viewType].viewList.data = newData;
  },

  deleteViewItem: (
    state: InitialStateType,
    action: PayloadAction<{ res: { view: { value: string } }; viewType: ViewTypes }>,
  ) => {
    const { viewType, res } = action.payload;
    const deletedId = res?.view?.value;
    state[viewType].viewList.data = state[viewType].viewList.data.filter(
      (item) => item?.view?.value !== deletedId,
    );
    // Remove from viewDetailsMap and viewDetailsReferenceMap
    delete state[viewType].viewDetailsMap[deletedId];
    delete state[viewType].viewDetailsReferenceMap[deletedId];
  },

  updateViewListWhenPinnedToNav: (
    state: InitialStateType,
    action: PayloadAction<{
      menuSlug: ViewTypes;
      res: {
        name: string;
        view_uuid: string;
        count: number;
        is_pinned: boolean;
      };
    }>,
  ) => {
    const newData = state[action?.payload?.menuSlug]?.viewList?.data?.map((item) => {
      if (item?.view?.value === action?.payload?.res?.view_uuid) {
        return { ...item, is_pinned: action?.payload?.res?.is_pinned };
      }
      return item;
    });
    state[action?.payload?.menuSlug].viewList.data = newData;
  },

  updateTechnicianInCalenderView: (
    state: InitialStateType,
    action: PayloadAction<{
      value: {
        label: string;
        value: string;
        user_id: string;
        timezone: string;
      };
      viewType: ViewTypes;
      viewId: string;
    }>,
  ) => {
    const { viewType, viewId, value } = action.payload;
    if (action?.payload && state[viewType]?.viewDetailsMap?.[viewId]) {
      state[viewType].viewDetailsMap[viewId].calendarFilter = {
        ...(state[viewType].viewDetailsMap[viewId]?.calendarFilter || {}),
        technician: value,
      };
    }
  },

  updateCalendarModeView: (
    state: InitialStateType,
    action: PayloadAction<{
      value: { label: string; value: string };
      viewType: ViewTypes;
      viewId: string;
    }>,
  ) => {
    const { viewType, viewId, value } = action.payload;
    if (action?.payload && state[viewType]?.viewDetailsMap?.[viewId]) {
      state[viewType].viewDetailsMap[viewId].calendarFilter = {
        ...state[viewType].viewDetailsMap[viewId]?.calendarFilter,
        calendarMode: value,
      };
    }
  },

  updateWorkingHoursInCalendarView: (
    state: InitialStateType,
    action: PayloadAction<{ value: boolean; viewType: ViewTypes; viewId: string }>,
  ) => {
    const { viewType, viewId, value } = action.payload;
    if (state[viewType]?.viewDetailsMap?.[viewId]) {
      state[viewType].viewDetailsMap[viewId].calendarFilter = {
        ...state[viewType].viewDetailsMap[viewId]?.calendarFilter,
        settings: {
          ...state[viewType].viewDetailsMap[viewId]?.calendarFilter?.settings,
          limitToWorkingHours: value,
        },
      };
    }
  },

  updateSelectedDateInCalendarView: (
    state: InitialStateType,
    action: PayloadAction<{ value: string; viewType: ViewTypes }>,
  ) => {
    if (action?.payload) {
      state[action?.payload?.viewType].calendarDatePickerSelectedDate = action?.payload?.value;
    }
  },

  updateViewDetails: (
    state: InitialStateType,
    action: PayloadAction<{
      viewDetailsResponse: ViewDetailsType;
      viewType: ViewTypes;
      viewId: string;
    }>,
  ) => {
    const { viewType, viewId, viewDetailsResponse } = action.payload;
    const config = withDrawnDefaultColumns(
      viewType,
      viewId,
      viewDetailsResponse || {},
      state[viewType].viewList?.data,
    );

    // Restore previous view's config from reference before switching
    const previousViewId = state[viewType].viewId;
    if (previousViewId && previousViewId !== viewId) {
      const referenceConfig = state[viewType].viewDetailsReferenceMap[previousViewId];
      if (referenceConfig) {
        state[viewType].viewDetailsMap[previousViewId] = { ...referenceConfig };
      }
    }

    state[viewType].viewDetailsMap[viewId] = { ...config };
    state[viewType].viewDetailsReferenceMap[viewId] = config;
    state[viewType].isViewDetailsFetched = true;
    state.fetchingViewDetails = false;
    state[viewType].viewId = viewId || '';
    state[viewType].search = { ...state[viewType].search, searchInput: '' };
    if (state[viewType].filtersDetails) {
      state[viewType].filtersDetails.filterComponent =
        config?.filters?.applied || ({} as AppliedFilterType);
    }
    state[viewType].hasListViewChanged = listViewInitialState?.[viewType]?.hasListViewChanged;
  },

  hydrateBootViewCache: (state: InitialStateType, action: PayloadAction<FetchAllViewsResponse>) => {
    const data = action.payload;
    (Object.keys(data) as string[]).forEach((viewType) => {
      const key = viewType as ViewTypes;
      if (!state[key]) return;
      const moduleData = data[viewType];
      if (!moduleData) return;

      state[key].viewList.data = moduleData.viewList ?? [];
      state[key].viewList.isViewListLoading = false;

      const detailsMap = Object.fromEntries(
        Object.entries(moduleData.viewDetailsMap ?? {}).map(([viewId, config]) => [
          viewId,
          withDrawnDefaultColumns(key, viewId, config, moduleData.viewList),
        ]),
      );
      // Before the first prefetch lands, an active view can only come from the stored config.
      const earlyViewId =
        !state.isViewsPrefetched && state[key].isViewDetailsFetched ? state[key].viewId : '';
      if (earlyViewId) {
        const freshConfig = detailsMap[earlyViewId];
        if (!freshConfig) {
          // Stored view no longer exists; fall back to the normal activation path.
          delete state[key].viewDetailsMap[earlyViewId];
          delete state[key].viewDetailsReferenceMap[earlyViewId];
          state[key].viewId = '';
          state[key].isViewDetailsFetched = false;
        } else if (!isEqual(freshConfig, state[key].viewDetailsReferenceMap[earlyViewId])) {
          if (state[key].filtersDetails) {
            state[key].filtersDetails.filterComponent =
              freshConfig?.filters?.applied || ({} as AppliedFilterType);
          }
        }
      }
      Object.keys(detailsMap).forEach((viewId) => {
        const config = detailsMap[viewId];
        // Unchanged early view keeps its references so the list isn't refetched.
        if (viewId === earlyViewId && isEqual(config, state[key].viewDetailsReferenceMap[viewId])) {
          return;
        }
        state[key].viewDetailsMap[viewId] = { ...config };
        state[key].viewDetailsReferenceMap[viewId] = config;
      });

      const defaultViewId =
        moduleData.defaultViewId ||
        moduleData.viewList?.find((item) => item.is_default)?.view?.value ||
        moduleData.viewList?.[0]?.view?.value;

      if (defaultViewId && detailsMap[defaultViewId] && !state[key].viewId) {
        const config = detailsMap[defaultViewId];
        state[key].viewId = defaultViewId;
        state[key].isViewDetailsFetched = true;
        if (state[key].filtersDetails) {
          state[key].filtersDetails.filterComponent =
            config?.filters?.applied || ({} as AppliedFilterType);
        }
      }
    });
    state.isViewsPrefetched = true;
    state.isViewsPrefetching = false;
  },

  // APX-478 — the boot prefetch settled as a failure. This is the ONLY producer of
  // `isViewsPrefetchFailed = true`; `fetchAllViews().rejected` deliberately no longer sets it
  // (see the case in `extra-reducers.ts` for why). Dispatched from the `fetchAllViews` catch in
  // `useUserProfile`, and only once the profile boot is known to have succeeded — otherwise the
  // rejection may be the whole boot failing rather than a missing endpoint.
  //
  // It also covers a shape the thunk's own lifecycle cannot: a 200 whose body is not the
  // expected map throws inside the `.then()` AFTER `fulfilled` has already run, and `fulfilled`
  // clears neither flag — so without this the `ListViewWrapper` skeleton would render forever
  // on a malformed response.
  markViewsPrefetchFailed: (state: InitialStateType) => {
    state.isViewsPrefetching = false;
    state.isViewsPrefetchFailed = true;
  },

  // APX-478 — abandon the boot prefetch attempt WITHOUT claiming the endpoint is unavailable.
  //
  // `isViewsPrefetchFailed` is not a marker, it is a trigger: `ListViewWrapper` reads it to
  // start a per-module fallback fetch and escalates any non-cancel rejection of that fallback
  // into `notFound()`. Once `fetchAllViews` runs in parallel with `getProfile` rather than
  // behind it, a backend blip that takes down the profile takes down the views call with it —
  // so the flag latches and a transient 5xx turns into a hard not-found page on every list
  // route. Clearing both flags when the profile boot fails restores exactly the pre-parallel
  // state (nothing fetched, nothing claimed) while `doSyncUser` retries.
  //
  // `isViewsPrefetched` is deliberately left alone: if the views response DID land, its cache
  // is still valid and there is no reason to throw it away because the profile failed.
  resetViewsPrefetchState: (state: InitialStateType) => {
    state.isViewsPrefetching = false;
    state.isViewsPrefetchFailed = false;
  },

  setSelectedGroupByDetailsData: (
    state: InitialStateType,
    action: PayloadAction<{ data: Array<GroupByDetailsOptionObj>; viewType: ViewTypes }>,
  ) => {
    const key = action?.payload?.viewType;

    if (key === 'quotes') {
      /**
       * Partially approved and approved is combined in approved status for quote list
       * Will remove partially approved on grouping
       */
      const pApprovedIndex = action?.payload?.data.findIndex(
        (status) => status.slug === 'partially-approved',
      );
      const approvedIndex = action?.payload?.data.findIndex((status) => status.slug === 'approved');
      if (pApprovedIndex !== -1 && approvedIndex !== -1) {
        // Update approved total
        const approvedItem = action?.payload?.data[approvedIndex] as GroupByDetailsOptionObj;
        const approvedTotal = action?.payload?.data[approvedIndex].total;
        const pTotal = action?.payload?.data[pApprovedIndex].total;
        approvedItem.total = approvedTotal + pTotal;
      }
      // Remove partially-approved
      state[key].groupByDetails = action?.payload?.data.filter(
        (status) => status.slug !== 'partially-approved',
      );
      const groupCollapseSettings = {} as { [key: string]: boolean };
      [...(action?.payload?.data || [])]?.forEach((item) => {
        groupCollapseSettings[item.slug] = false;
      });
      state[action?.payload?.viewType].groupCollapseSettings = groupCollapseSettings;
    } else if (key === 'work-orders') {
      /**
       * Transform label for not-assigned group to display as "No Provider"
       */
      const transformedData = action?.payload?.data.map((item) => {
        if (item.slug === 'not-assigned') {
          return {
            ...item,
            label: 'No Provider',
          };
        }
        return item;
      });
      state[key].groupByDetails = transformedData;
      const groupCollapseSettings = {} as { [key: string]: boolean };
      transformedData.forEach((item) => {
        groupCollapseSettings[item.slug] = false;
      });
      state[action?.payload?.viewType].groupCollapseSettings = groupCollapseSettings;
    } else {
      state[key].groupByDetails = action?.payload?.data;
      const groupCollapseSettings = {} as { [key: string]: boolean };
      [...(action?.payload?.data || [])]?.forEach((item) => {
        groupCollapseSettings[item.slug] = false;
      });
      state[action?.payload?.viewType].groupCollapseSettings = groupCollapseSettings;
    }
  },

  updateSelectedGroupByDetailsData: (
    state: InitialStateType,
    action: PayloadAction<{ data: Array<GroupByDetailsOptionObj>; viewType: ViewTypes }>,
  ) => {
    const key = action?.payload?.viewType;

    // Transform label for not-assigned group in work-orders
    const transformedData =
      key === 'work-orders'
        ? action?.payload?.data.map((item: GroupByDetailsOptionObj) => {
            if (item.slug === 'not-assigned') {
              return {
                ...item,
                label: 'No Provider',
              };
            }
            return item;
          })
        : action?.payload?.data;

    state[key].groupByDetails = transformedData;
    const groupCollapseSettings = {} as { [key: string]: boolean };
    transformedData.forEach((item: GroupByDetailsOptionObj) => {
      groupCollapseSettings[item.slug] = state[key]?.groupCollapseSettings?.[item.slug] || false;
    });
    state[key].groupCollapseSettings = groupCollapseSettings;
  },

  setFilterFieldValueData: (
    state: InitialStateType,
    action: PayloadAction<{ data: []; viewType: ViewTypes; fieldValue: string }>,
  ) => {
    /**
     * Patially approved and approved in quotes list are combined into approved status
     */
    if (action?.payload?.viewType === 'quotes' && action?.payload?.fieldValue === 'status') {
      const updatedStatuses = action?.payload?.data.filter(
        (status: QuoteFilterStatus) => status.value !== 'partially-approved',
      );
      state[action?.payload?.viewType].filtersDetails.valuesLookup[action?.payload?.fieldValue] =
        updatedStatuses;
    } else {
      state[action?.payload?.viewType].filtersDetails.valuesLookup[action?.payload?.fieldValue] =
        action?.payload?.data;
    }
  },

  restListViewChangeWatcher: (state: InitialStateType, action: PayloadAction<ViewTypes>) => {
    const viewType = action.payload;
    const viewId = state[viewType].viewId;
    state[viewType].hasListViewChanged = listViewInitialState?.[viewType]?.hasListViewChanged;
    // Sync reference to match current config after a successful save
    const currentConfig = state[viewType].viewDetailsMap[viewId];
    if (viewId && currentConfig) {
      state[viewType].viewDetailsReferenceMap[viewId] = { ...currentConfig };
    }
  },

  updateFilterValueLookup: (
    state: InitialStateType,
    action: PayloadAction<{
      data: {
        label: string;
        value: string;
      };
      viewType: ViewTypes;
      fieldValue: string;
      actionType: 'add' | 'remove';
    }>,
  ) => {
    const { data, viewType, fieldValue, actionType } = action.payload;
    const filterLookup = { ...(state[viewType]?.filtersDetails?.valuesLookup || {}) };
    if (!isEmpty(filterLookup?.[fieldValue])) {
      if (actionType === 'add') {
        // update the object with latest data if already present
        if (filterLookup?.[fieldValue]?.find((item) => item?.value === data?.value)) {
          filterLookup[fieldValue] = filterLookup?.[fieldValue]?.map((item) => {
            if (item?.value === data?.value) {
              return data;
            }
            return item;
          });
        } else {
          // add new data to the array
          filterLookup[fieldValue] = [...(filterLookup?.[fieldValue] || []), data];
        }
      } else if (actionType === 'remove') {
        filterLookup[fieldValue] = filterLookup?.[fieldValue]?.filter(
          (item) => item?.value !== data?.value,
        );
      }
      state[viewType].filtersDetails.valuesLookup = filterLookup;
    }
  },

  setSelectedMarkets: (
    state: InitialStateType,
    action: PayloadAction<Array<{ label: string; value: string }>>,
  ) => {
    state.selectedMarkets = action.payload;
  },

  seedSelectedMarkets: (
    state: InitialStateType,
    action: PayloadAction<Array<{ id: string; name: string }> | undefined>,
  ) => {
    seedMarkets(state, action.payload);
  },

  setColumnsForRoles: (
    state: InitialStateType,
    action: PayloadAction<{ data: ColumnObjType[] }>,
  ) => {
    const { data } = action.payload;
    if (!state.roles.viewDetailsMap['roles']) {
      state.roles.viewDetailsMap['roles'] = {} as ViewDetailsType;
    }
    state.roles.viewDetailsMap['roles'].columns = data;
    state.roles.viewId = 'roles';
    state.roles.isViewDetailsFetched = true;
  },
};

export default reducers;
